<script lang="ts" setup>
import type { DateTime } from 'luxon';
import type { AutocompleteListModel as ProducerListModel } from '../../../pages/store/catalog/producers/_runtime/services/ProducersService';
import ProducersService from '../../../pages/store/catalog/producers/_runtime/services/ProducersService';

defineOptions({
    name: 'WidgetProducersSettings'
});

interface FormSettings
{
    settings: {
        datePublishedFromUtc?: DateTime;
        datePublishedDueUtc?: DateTime;
        format: string;
        producersIds: number[];
    };
}

const model = defineModel<FormType<FormSettings>>();
const defaults: FormSettings['settings'] = {
    format: 'slider',
    producersIds: []
};

const form = computed(() => model.value);

onMounted(async () =>
{
    form.value.settings = { ...defaults, ...form.value.settings };
});

const producerId = ref<number>(null);
const producers = computedAsync(async () =>
{
    try
    {
        if (form.value.settings.producersIds.length === 0)
            return [];

        return await ProducersService.getListByIds(form.value.settings.producersIds);
    }
    catch (ex)
    {
        return [];
    }
});

const formatOptions: Option[] = [
    { value: 'slider', text: $t('[[[slider]]]') },
    { value: 'grid', text: $t('[[[grid]]]') }
];

const fetchProducer = async (id: number): Promise<ProducerListModel> =>
{
    const response = await ProducersService.fetchAutocompleteModel(id);

    return response.first() ?? null;
};

const searchProducer = async (query: string, limit: number): Promise<ProducerListModel[]> =>
{
    return await ProducersService.getAutocompleteList(query, limit);
};

const addProducer = async (): Promise<void> =>
{
    if (producerId.value == null)
        return;

    form.value.settings.producersIds.push(producerId.value);
    producerId.value = null;
};

const removeProducer = async (id: number): Promise<void> =>
{
    form.value.settings.producersIds = form.value.settings.producersIds.filter(p => p !== id);
};

async function validate()
{
    form.value.validate(schema => ({
        settings: schema.object({
            producersIds: schema.array(schema.number().required($t('[[[Wybranie producenta jest wymagane]]]'))).min(1, $t('[[[Wybranie producenta jest wymagane]]]')).typeError($t('[[[Wybranie producenta jest wymagane]]]'))
        })
    }));
}

watch(() => form.value.settings.producersIds, () =>
{
    form.value.$errors.clear('settings.producersIds');
}, { deep: true });

defineExpose({
    validate
});
</script>

<template>
    <IdeoFormGroup>
        <div class="row">
            <div class="col-md-6">
                <IdeoFormGroup :label="$t('[[[Publikuj od]]]')" :invalid-feedback="form.$errors.first('settings.datePublishedFromUtc')" :state="form.$errors.state('settings.datePublishedFromUtc')" required>
                    <IdeoDatetime v-model="form.settings.datePublishedFromUtc" name="datePublishedFromUtc" type="datetime" />
                </IdeoFormGroup>
            </div>
            <div class="col-md-6">
                <IdeoFormGroup :label="$t('[[[Publikuj do]]]')" :invalid-feedback="form.$errors.first('settings.datePublishedDueUtc')" :state="form.$errors.state('settings.datePublishedDueUtc')">
                    <IdeoDatetime v-model="form.settings.datePublishedDueUtc" name="datePublishedDueUtc" type="datetime" />
                </IdeoFormGroup>
            </div>
        </div>

        <IdeoFormGroup :label="$t('[[[Szablon]]]')" :invalid-feedback="form.$errors.first('settings.format')" :state="form.$errors.state('settings.format')">
            <IdeoFormSelect v-model="form.settings.format" :options="formatOptions" />
        </IdeoFormGroup>

        <IdeoFormGroup :label="$t('[[[Wybierz producentów]]]')" :invalid-feedback="form.$errors.first('settings.producersIds')" :state="form.$errors.state('settings.producersIds')">
            <div class="d-flex flex-column flex-md-row">
                <div class="flex-grow-1">
                    <IdeoAutocomplete
                        v-model="producerId"
                        name="producerId"
                        track-by="id"
                        :placeholder="$t('[[[zacznij pisać aby wyszukać producenta...]]]')"
                        :fetch="(id: number) => fetchProducer(id)"
                        :search="(query: string, limit: number) => searchProducer(query, limit)"
                    >
                        <template #selected="{ option }">
                            <div class="text-wrap">
                                {{ option.name }}
                            </div>
                        </template>
                        <template #option="{ option }">
                            <div class="text-wrap">
                                {{ option.name }}
                            </div>
                        </template>
                    </IdeoAutocomplete>
                </div>
                <div class="d-flex align-items-stretch">
                    <button class="btn btn-secondary text-nowrap mt-2 mt-md-0 ms-md-2" @click.prevent="addProducer()">
                        <i class="fas fa-plus" />
                    </button>
                </div>
            </div>
        </IdeoFormGroup>

        <h5 class="mb-3">
            {{ $t('[[[Producenci]]]') }}
        </h5>
        <IdeoFormGroup v-if="producers">
            <div class="list-group">
                <div v-for="item in producers" :key="item.id" class="list-group-item list-group-item-action p-2 d-flex">
                    <div class="d-flex align-items-center justify-content-between gap-2 w-100">
                        <div class="d-flex align-items-center gap-2">
                            <i class="fad fa-fw fa-browser text-primary" />
                            <b :title="$t('[[[Nazwa]]]')">
                                {{ item.name }}
                            </b>
                        </div>

                        <div class="d-flex align-items-center">
                            <Confirmation :message="$t('[[[Potwierdzenie usunięcia]]]')" :target="`btn-delete-producer-${item.id}`" :value="item.id" @confirm="removeProducer(item.id)" />
                            <IdeoButton :id="`btn-delete-producer-${item.id}`" variant="danger" size="sm" pill :title="$t('[[[Usuń producenta]]]')">
                                <i class="fas fa-trash-alt" />
                            </IdeoButton>
                        </div>
                    </div>
                </div>
                <div v-if="form.settings.producersIds.length == 0" class="list-group-item list-group-item-light text-center">
                    {{ $t('[[[Brak wybranych producentów]]]') }}
                </div>
            </div>
        </IdeoFormGroup>
    </IdeoFormGroup>
</template>
